import { account } from '../core/account.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { qrSvg } from '../core/external.js';

/* Verificación en dos pasos (TOTP) de los administradores: el código que se pide al iniciar sesión y el alta/baja desde Ajustes.
   El servidor hace las comprobaciones (server/totp.js); aquí solo está la interfaz. */

const codeInput = (dlg) => dlg.$el.find('input').val();

function tuneInput(dlg) {
    const input = dlg.$el.find('input')[0];
    if (!input) return;
    input.setAttribute('autocomplete', 'one-time-code');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('maxlength', '12');
    setTimeout(() => { try { input.focus(); } catch (e) { /* sin foco */ } }, 120);
}

/** Pide el código de la aplicación (o uno de recuperación) al iniciar sesión. Devuelve el texto, o null si se cancela. */
export function askSecondFactor({ retry = false } = {}) {
    return new Promise((resolve) => {
        const dlg = window.app.dialog.prompt(t(retry ? 'twofa.askRetry' : 'twofa.askText'), t('twofa.askTitle'), (v) => resolve(String(v || '').trim() || null), () => resolve(null), '');
        tuneInput(dlg);
    });
}

function showRecoveryCodes(codes, onClose) {
    const dlg = window.app.dialog.create({
        title: t('twofa.recoveryTitle'),
        text: `<p>${escapeHtml(t('twofa.recoveryText'))}</p><pre class="twofa-codes">${codes.map(escapeHtml).join('\n')}</pre>`,
        buttons: [
            { text: t('twofa.copy'), close: false, onClick: () => { try { navigator.clipboard.writeText(codes.join('\n')); notify.toast(t('twofa.copied'), { icon: 'content_copy' }); } catch (e) { /* sin portapapeles: se pueden copiar a mano */ } } },
            { text: t('common.close'), bold: true, onClick: () => { if (onClose) onClose(); } }
        ]
    });
    dlg.open();
}

async function enable(onChange) {
    let start;
    try { start = await account.request('POST', '/api/account/2fa/start'); } catch (e) { notify.error(t('login.pw.error.generic')); return; }
    const dlg = window.app.dialog.create({
        title: t('twofa.enableTitle'),
        text: `<p>${escapeHtml(t('twofa.enableText'))}</p><div class="twofa-qr" style="width:180px;margin:8px auto;background:#fff;padding:8px;border-radius:8px">${qrSvg(start.otpauth)}</div>`
            + `<p class="twofa-secret" style="word-break:break-all;user-select:all;text-align:center"><small>${escapeHtml(t('twofa.secretLabel'))}</small><br><b>${escapeHtml(start.secret)}</b></p>`
            + `<div class="dialog-input-field item-input"><div class="item-input-wrap"><input class="dialog-input" type="text" inputmode="numeric" placeholder="${escapeHtml(t('twofa.codePlaceholder'))}"></div></div>`,
        buttons: [
            { text: t('common.cancel') },
            {
                text: t('twofa.activate'), bold: true, close: false,
                onClick: async () => {
                    const code = String(codeInput(dlg) || '').trim();
                    if (!code) return;
                    try {
                        const r = await account.request('POST', '/api/account/2fa/confirm', { body: { pending: start.pending, code } });
                        dlg.close();
                        notify.toast(t('twofa.enabledToast'), { icon: 'verified_user' });
                        try { await account.sync(); } catch (e) { /* se verá al recargar */ }
                        showRecoveryCodes(r.recoveryCodes || [], onChange);
                    } catch (e) {
                        if (e && e.code === 'twofa_expired') { dlg.close(); notify.error(t('login.pw.error.twofa_expired')); return; }
                        notify.error(t('twofa.invalid'));
                    }
                }
            }
        ]
    });
    dlg.open();
    tuneInput(dlg);
}

function disable(onChange) {
    const dlg = window.app.dialog.prompt(t('twofa.disableText'), t('twofa.disable'), async (code) => {
        try {
            await account.request('POST', '/api/account/2fa/disable', { body: { code: String(code || '').trim() } });
            try { await account.sync(); } catch (e) { /* se verá al recargar */ }
            notify.toast(t('twofa.disabledToast'), { icon: 'lock_open' });
            if (onChange) onChange();
        } catch (e) {
            notify.error(e && e.code === 'too_many_attempts' ? t('login.pw.error.too_many_attempts') : t('twofa.invalid'));
        }
    }, null, '');
    tuneInput(dlg);
}

/** Ajustes → Verificación en dos pasos (solo administradores): activarla con un QR o desactivarla con un código. */
export async function openTwoFactor({ onChange } = {}) {
    let st;
    try { st = await account.request('GET', '/api/account/2fa'); } catch (e) { notify.error(t('common.errorNetwork')); return; }
    if (!st.available) return;
    if (!st.enabled) { enable(onChange); return; }
    const dlg = window.app.dialog.create({
        title: t('twofa.title'),
        text: `<p>${escapeHtml(t('twofa.statusText', { n: st.recoveryLeft }))}</p>`,
        buttons: [{ text: t('twofa.disable'), color: 'red', onClick: () => disable(onChange) }, { text: t('common.close'), bold: true }]
    });
    dlg.open();
}
